import { useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { PdfCanvas } from "./PdfCanvas";
import { useLanguage } from "@/components/language-provider";

interface PdfPreviewProps {
  /** PDF URL to render (fragment params like #toolbar are not needed), or a File already in memory */
  src: string | File;
  className?: string;
  /** Max height of the scrollable page area (default 480) */
  maxHeight?: number;
  /** Starting zoom (default 1 = fit to width) */
  initialZoom?: number;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

/**
 * Self-contained PDF preview: renders pages via pdf.js canvas (PdfCanvas)
 * with zoom (the page is re-rendered at the new resolution, never CSS-scaled)
 * and a pager. Zoomed pages scroll sideways inside the frame. Works identically
 * across browsers - no native <iframe>/<object> PDF viewer involved.
 */
export function PdfPreview({ src, className, maxHeight = 480, initialZoom = 1 }: PdfPreviewProps) {
  const { t } = useLanguage();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(1);
  const [zoom, setZoom] = useState(initialZoom);
  const step = (delta: number) => setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z + delta).toFixed(2))));

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2 text-sm" data-testid="pdf-zoom">
        <button type="button" className="px-2 py-1 rounded border disabled:opacity-40" aria-label={t("pdf.zoomOut")}
          disabled={zoom <= ZOOM_MIN} onClick={() => step(-ZOOM_STEP)} data-testid="pdf-zoom-out">
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="text-xs font-medium min-w-[40px] text-center" data-testid="pdf-zoom-value">{Math.round(zoom * 100)}%</span>
        <button type="button" className="px-2 py-1 rounded border disabled:opacity-40" aria-label={t("pdf.zoomIn")}
          disabled={zoom >= ZOOM_MAX} onClick={() => step(ZOOM_STEP)} data-testid="pdf-zoom-in">
          <ZoomIn className="h-4 w-4" />
        </button>
        {zoom !== 1 && (
          <button type="button" className="px-2 py-1 rounded text-xs text-muted-foreground hover:underline" onClick={() => setZoom(1)}>
            100%
          </button>
        )}
      </div>
      <div className="bg-white rounded border overflow-auto" style={{ maxHeight }} data-testid="pdf-scroll">
        <PdfCanvas src={src} pageNumber={page} onPageCount={setCount} zoom={zoom} />
      </div>
      {count > 1 && (
        <div className="flex items-center justify-center gap-3 mt-2 text-sm">
          <button type="button" className="px-2 py-1 rounded border disabled:opacity-40"
            disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>{"←"}</button>
          <span className="text-muted-foreground">{page + 1} / {count}</span>
          <button type="button" className="px-2 py-1 rounded border disabled:opacity-40"
            disabled={page >= count - 1} onClick={() => setPage((p) => p + 1)}>{"→"}</button>
        </div>
      )}
    </div>
  );
}
